import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Check, X, Camera, CaretDown, CaretUp, Plus, Trash, Snowflake, Users, Ruler, Bed, ForkKnife } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VenueSelector = ({ onSave }) => {
  const [venueTypes, setVenueTypes] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [selectedVenues, setSelectedVenues] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [uploadTarget, setUploadTarget] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/quick-add-items?vendor_category=venue`);
      setVenueTypes(data.filter(i => i.item_type === 'venue_type'));
    } catch { /* fallback empty */ }
    setLoaded(true);
  };

  // Should the "Marriage / Engagement / Reception" price field show for this
  // venue type? Hidden when the venue's event_restriction excludes marriage
  // (e.g. Kids Play Area = birthday only, Bar / Cafe = excludes marriage).
  const allowsWeddingPrice = (vt) => {
    const r = vt?.event_restriction || {};
    const events = (r.events || []).map(e => String(e).toLowerCase());
    if (r.type === 'only') return events.includes('marriage');
    if (r.type === 'exclude') return !events.includes('marriage');
    return true;
  };
  // Should the "Other events" price field show? Hidden when the venue is
  // strictly marriage-only (none of the current catalogue uses this, but
  // future-proofed) — i.e. restriction = only [marriage].
  const allowsOtherEventsPrice = (vt) => {
    const r = vt?.event_restriction || {};
    const events = (r.events || []).map(e => String(e).toLowerCase());
    if (r.type === 'only') {
      // Only allowed events are wedding-tier? then hide other_events.
      const weddingTier = ['marriage', 'engagement', 'reception', 'wedding'];
      return events.some(e => !weddingTier.includes(e));
    }
    return true;
  };

  const makeEmptyVenue = (name, restriction) => ({
    name,
    expanded: true,
    photos: [],
    has_ac: false,
    sqft: '',
    guest_capacity: '',
    has_rooms: false,
    single_rooms: 0,
    double_rooms: 0,
    has_catering: false,
    min_guests: '',
    // Stash restriction so customer-side filtering stays consistent. Frontend
    // doesn't use this directly — backend already filters by restriction.
    event_restriction: restriction || { type: 'any' },
    // Both prices are OPTIONAL but at least ONE must be set:
    //  - marriage covers wedding / engagement / reception
    //  - other_events covers everything else
    event_prices: {
      marriage: '',
      other_events: '',
    },
    show_other_events: false,
    catering_tiers: {
      budget: { morning_tiffin: '', lunch_veg: '', lunch_nonveg: '', evening_snacks: '', dinner_veg: '', dinner_nonveg: '', juice: '' },
      premium: { morning_tiffin: '', lunch_veg: '', lunch_nonveg: '', evening_snacks: '', dinner_veg: '', dinner_nonveg: '', juice: '' },
      luxury: { morning_tiffin: '', lunch_veg: '', lunch_nonveg: '', evening_snacks: '', dinner_veg: '', dinner_nonveg: '', juice: '' },
    },
  });

  const toggleVenue = (vt) => {
    const exists = selectedVenues.find(v => v.name === vt.name);
    if (exists) {
      setSelectedVenues(selectedVenues.filter(v => v.name !== vt.name));
    } else {
      const allowsMarriage = allowsWeddingPrice(vt);
      const allowsOther = allowsOtherEventsPrice(vt);
      const fresh = makeEmptyVenue(vt.name, vt.event_restriction);
      // If the venue type doesn't permit wedding-tier events, default-open the
      // "other events" price block so vendor immediately sees the only field
      // they're allowed to set.
      if (!allowsMarriage && allowsOther) fresh.show_other_events = true;
      setSelectedVenues([...selectedVenues, fresh]);
    }
  };

  const toggleExpand = (idx) => {
    const u = [...selectedVenues];
    u[idx].expanded = !u[idx].expanded;
    setSelectedVenues(u);
  };

  const updateField = (idx, field, value) => {
    const u = [...selectedVenues];
    u[idx][field] = value;
    setSelectedVenues(u);
  };

  const updateCateringTier = (idx, tier, type, value) => {
    const u = [...selectedVenues];
    u[idx].catering_tiers[tier][type] = value;
    setSelectedVenues(u);
  };

  const updateEventPrice = (idx, eventKey, value) => {
    const u = [...selectedVenues];
    u[idx].event_prices[eventKey] = value;
    setSelectedVenues(u);
  };

  // Photo upload
  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !uploadTarget) return;
    setUploading(uploadTarget.key);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      const resp = await axios.post(`${BACKEND_URL}/api/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      const url = resp.data.url;
      const { vIdx } = uploadTarget;
      const updated = [...selectedVenues];
      if (updated[vIdx].photos.length < 10) {
        updated[vIdx].photos.push(url);
        setSelectedVenues(updated);
        toast.success('Photo uploaded!');
      }
    } catch {
      toast.error('Upload failed');
    }
    setUploading(null);
    setUploadTarget(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePhoto = (vIdx, pIdx) => {
    const u = [...selectedVenues];
    u[vIdx].photos = u[vIdx].photos.filter((_, i) => i !== pIdx);
    setSelectedVenues(u);
  };

  const triggerUpload = (target) => {
    setUploadTarget(target);
    setTimeout(() => fileInputRef.current?.click(), 50);
  };

  const handleSaveAll = () => {
    if (selectedVenues.length === 0) {
      toast.error('Select at least one venue type');
      return;
    }
    // Validate required fields
    for (const v of selectedVenues) {
      if (!v.sqft) { toast.error(`${v.name}: Square feet is required`); return; }
      if (!v.guest_capacity) { toast.error(`${v.name}: Guest capacity is required`); return; }
      const marriagePrice = Number(v.event_prices?.marriage || 0);
      const otherPrice = Number(v.event_prices?.other_events || 0);
      if (marriagePrice <= 0 && otherPrice <= 0) {
        toast.error(`${v.name}: Enter at least one price — Wedding or Other Events`);
        return;
      }
    }
    onSave({ venues: selectedVenues });
  };

  const PhotoBox = ({ photoUrl, uploadKey, onTrigger, size = 'md' }) => {
    const sizeClass = size === 'lg' ? 'w-20 h-20' : 'w-12 h-12';
    return (
      <div
        className={`${sizeClass} border border-dashed border-[#E8E1D5] rounded flex-shrink-0 flex items-center justify-center cursor-pointer overflow-hidden bg-white hover:border-brand-primary transition-colors`}
        onClick={onTrigger}
        data-testid={`photo-box-${uploadKey}`}
      >
        {uploading === uploadKey ? (
          <span className="text-xs text-text-secondary">...</span>
        ) : photoUrl ? (
          <img src={`${BACKEND_URL}${photoUrl}`} alt="" className="w-full h-full object-cover" />
        ) : (
          <Camera size={size === 'lg' ? 24 : 18} className="text-text-secondary" />
        )}
      </div>
    );
  };

  if (!loaded) return <div className="text-center py-8 text-text-secondary">Loading venue types...</div>;

  return (
    <div className="space-y-3">
      <input type="file" accept="image/*" ref={fileInputRef} onChange={handlePhotoUpload} className="hidden" />
      <p className="text-sm text-text-secondary">Select venue types you offer. Add photos, capacity details, and catering options.</p>

      {venueTypes.map((vt, idx) => {
        const selected = selectedVenues.find(v => v.name === vt.name);
        const vIdx = selectedVenues.findIndex(v => v.name === vt.name);

        return (
          <div key={idx} className={`border-2 rounded-md transition-all ${selected ? 'border-green-500 bg-green-50/30' : 'border-[#E8E1D5] bg-white'}`}>
            {/* Venue type header */}
            <div className="flex items-center gap-3 p-3" data-testid={`toggle-venue-${vt.name.toLowerCase().replace(/[ /]/g, '-')}`}>
              <Button
                type="button" onClick={() => toggleVenue(vt)}
                className={`rounded-full w-10 h-10 p-0 flex-shrink-0 ${selected ? 'bg-green-600 hover:bg-green-700' : 'bg-red-500 hover:bg-red-600'} text-white`}
              >
                {selected ? <Check size={18} weight="bold" /> : <X size={18} weight="bold" />}
              </Button>
              <div className="flex-1 font-medium cursor-pointer" onClick={() => selected && toggleExpand(vIdx)}>
                {vt.name}
                {selected && (
                  <span className="text-xs text-text-secondary ml-2">
                    ({selected.photos.length} photos)
                  </span>
                )}
              </div>
              {selected && (
                <Button type="button" variant="ghost" size="sm" onClick={() => toggleExpand(vIdx)}>
                  {selected.expanded ? <CaretUp size={16} /> : <CaretDown size={16} />}
                </Button>
              )}
            </div>

            {/* Expanded content */}
            {selected && selected.expanded && (
              <div className="border-t border-[#E8E1D5] px-4 py-4 space-y-5 bg-white/50">

                {/* Photos (up to 10) */}
                <div>
                  <div className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary mb-2">
                    Venue Photos (up to 10)
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selected.photos.map((photo, pIdx) => (
                      <div key={pIdx} className="relative group">
                        <div className="w-20 h-20 border border-[#E8E1D5] rounded overflow-hidden">
                          <img src={`${BACKEND_URL}${photo}`} alt="" className="w-full h-full object-cover" />
                        </div>
                        <button
                          type="button"
                          onClick={() => removePhoto(vIdx, pIdx)}
                          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X size={10} weight="bold" />
                        </button>
                      </div>
                    ))}
                    {selected.photos.length < 10 && (
                      <div
                        className="w-20 h-20 border-2 border-dashed border-brand-primary/30 rounded flex flex-col items-center justify-center cursor-pointer hover:border-brand-primary hover:bg-brand-accent/5 transition-all"
                        onClick={() => triggerUpload({ key: `venue-${vIdx}`, vIdx })}
                        data-testid={`add-photo-venue-${vIdx}`}
                      >
                        <Plus size={18} className="text-brand-primary mb-1" />
                        <span className="text-[10px] text-text-secondary">Add</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* AC & Basic Details */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* AC */}
                  <div
                    className={`flex items-center gap-3 p-3 border-2 rounded cursor-pointer transition-all ${selected.has_ac ? 'border-green-500 bg-green-50' : 'border-[#E8E1D5]'}`}
                    onClick={() => updateField(vIdx, 'has_ac', !selected.has_ac)}
                    data-testid={`ac-toggle-${vIdx}`}
                  >
                    <div className={`rounded-full w-8 h-8 flex items-center justify-center ${selected.has_ac ? 'bg-green-600' : 'bg-red-500'} text-white`}>
                      {selected.has_ac ? <Check size={14} weight="bold" /> : <X size={14} weight="bold" />}
                    </div>
                    <Snowflake size={20} className="text-blue-500" />
                    <span className="text-sm font-medium">Air Conditioned</span>
                  </div>

                  {/* Square Feet */}
                  <div className="space-y-1">
                    <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary flex items-center gap-1">
                      <Ruler size={14} /> Sq. Feet <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      type="number"
                      value={selected.sqft}
                      onChange={(e) => updateField(vIdx, 'sqft', e.target.value)}
                      placeholder="e.g. 5000"
                      className="rounded-none border-[#E8E1D5] h-9"
                      data-testid={`sqft-input-${vIdx}`}
                    />
                  </div>

                  {/* Guest Capacity */}
                  <div className="space-y-1">
                    <Label className="text-xs tracking-[0.2em] uppercase font-bold text-text-secondary flex items-center gap-1">
                      <Users size={14} /> Max Guests <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      type="number"
                      value={selected.guest_capacity}
                      onChange={(e) => updateField(vIdx, 'guest_capacity', e.target.value)}
                      placeholder="e.g. 500"
                      className="rounded-none border-[#E8E1D5] h-9"
                      data-testid={`guests-input-${vIdx}`}
                    />
                  </div>
                </div>

                {/* Venue Rental Pricing per Event */}
                <div className="border-2 border-brand-primary/40 rounded p-3 bg-brand-accent/5">
                  <div className="text-xs tracking-[0.2em] uppercase font-bold text-brand-primary mb-1">
                    Venue Rental Pricing
                  </div>
                  <p className="text-xs text-text-secondary mb-3 leading-relaxed">
                    Both prices are <strong>optional</strong> — fill at least one. Wedding price covers <strong>Wedding, Engagement &amp; Reception</strong>; Other Events price covers everything else. The venue will only be listed for events whose price you set.
                  </p>
                  {/* Wedding — optional, hidden if venue type excludes wedding-tier */}
                  {allowsWeddingPrice(vt) && (
                    <div className="space-y-1 mb-3">
                      <Label className="text-xs font-bold text-text-secondary">
                        Wedding / Engagement / Reception <span className="text-text-secondary font-normal">(optional)</span>
                      </Label>
                      <Input
                        type="number"
                        value={selected.event_prices.marriage}
                        onChange={(e) => updateEventPrice(vIdx, 'marriage', e.target.value)}
                        placeholder="₹ rental price for wedding-tier events"
                        className="rounded-none h-9 border-[#E8E1D5]"
                        data-testid={`event-price-marriage-${vIdx}`}
                      />
                    </div>
                  )}
                  {/* Toggle for other event prices — auto-open when wedding is not allowed */}
                  {allowsOtherEventsPrice(vt) && allowsWeddingPrice(vt) && (
                    <button
                      type="button"
                      onClick={() => updateField(vIdx, 'show_other_events', !selected.show_other_events)}
                      className="text-xs font-bold text-brand-primary hover:underline flex items-center gap-1"
                      data-testid={`toggle-other-events-${vIdx}`}
                    >
                      {selected.show_other_events ? <CaretUp size={12} /> : <CaretDown size={12} />}
                      {selected.show_other_events ? 'Hide other event prices' : '+ Add prices for other events (optional)'}
                    </button>
                  )}
                  {allowsOtherEventsPrice(vt) && (selected.show_other_events || !allowsWeddingPrice(vt)) && (
                    <div className={allowsWeddingPrice(vt) ? "mt-3 pt-3 border-t border-[#E8E1D5] space-y-2" : "space-y-2"}>
                      <Label className="text-xs font-bold text-text-secondary">
                        Other Events Price <span className="text-text-secondary font-normal">(optional)</span>
                      </Label>
                      <Input
                        type="number"
                        value={selected.event_prices.other_events}
                        onChange={(e) => updateEventPrice(vIdx, 'other_events', e.target.value)}
                        placeholder="₹ price for Birthday, Anniversary, Corporate, etc."
                        className="rounded-none border-[#E8E1D5] h-9 text-sm"
                        data-testid={`event-price-other-${vIdx}`}
                      />
                      <p className="text-[10px] text-text-secondary italic">
                        Applies to Birthday, Baby Shower, House Warming, Anniversary, Corporate, Puberty, Retirement, Religious, and any other event. Leave blank to keep this venue out of non-wedding listings.
                      </p>
                    </div>
                  )}
                </div>

                {/* Rooms Section (Optional) */}
                <div className={`border-2 rounded p-3 transition-all ${selected.has_rooms ? 'border-green-400 bg-green-50/30' : 'border-[#E8E1D5]'}`}>
                  <div
                    className="flex items-center gap-3 cursor-pointer"
                    onClick={() => updateField(vIdx, 'has_rooms', !selected.has_rooms)}
                    data-testid={`rooms-toggle-${vIdx}`}
                  >
                    <div className={`rounded-full w-8 h-8 flex items-center justify-center ${selected.has_rooms ? 'bg-green-600' : 'bg-red-500'} text-white`}>
                      {selected.has_rooms ? <Check size={14} weight="bold" /> : <X size={14} weight="bold" />}
                    </div>
                    <Bed size={20} className="text-amber-600" />
                    <span className="text-sm font-medium">Rooms Available for Stay</span>
                    <span className="text-xs text-text-secondary">(Optional)</span>
                  </div>
                  {selected.has_rooms && (
                    <div className="grid grid-cols-2 gap-4 mt-3 pl-11">
                      <div className="space-y-1">
                        <Label className="text-xs font-bold text-text-secondary">Single Bedrooms</Label>
                        <div className="flex items-center gap-2">
                          <Button type="button" variant="outline" size="sm" className="w-8 h-8 p-0 rounded-none border-[#E8E1D5]"
                            onClick={() => updateField(vIdx, 'single_rooms', Math.max(0, selected.single_rooms - 1))}>
                            <span className="text-lg">-</span>
                          </Button>
                          <span className="w-8 text-center font-bold" data-testid={`single-rooms-count-${vIdx}`}>{selected.single_rooms}</span>
                          <Button type="button" variant="outline" size="sm" className="w-8 h-8 p-0 rounded-none border-[#E8E1D5]"
                            onClick={() => updateField(vIdx, 'single_rooms', selected.single_rooms + 1)}>
                            <span className="text-lg">+</span>
                          </Button>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs font-bold text-text-secondary">Double Bedrooms</Label>
                        <div className="flex items-center gap-2">
                          <Button type="button" variant="outline" size="sm" className="w-8 h-8 p-0 rounded-none border-[#E8E1D5]"
                            onClick={() => updateField(vIdx, 'double_rooms', Math.max(0, selected.double_rooms - 1))}>
                            <span className="text-lg">-</span>
                          </Button>
                          <span className="w-8 text-center font-bold" data-testid={`double-rooms-count-${vIdx}`}>{selected.double_rooms}</span>
                          <Button type="button" variant="outline" size="sm" className="w-8 h-8 p-0 rounded-none border-[#E8E1D5]"
                            onClick={() => updateField(vIdx, 'double_rooms', selected.double_rooms + 1)}>
                            <span className="text-lg">+</span>
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Catering Section (Optional) */}
                <div className={`border-2 rounded p-3 transition-all ${selected.has_catering ? 'border-green-400 bg-green-50/30' : 'border-[#E8E1D5]'}`}>
                  <div
                    className="flex items-center gap-3 cursor-pointer"
                    onClick={() => updateField(vIdx, 'has_catering', !selected.has_catering)}
                    data-testid={`catering-toggle-${vIdx}`}
                  >
                    <div className={`rounded-full w-8 h-8 flex items-center justify-center ${selected.has_catering ? 'bg-green-600' : 'bg-red-500'} text-white`}>
                      {selected.has_catering ? <Check size={14} weight="bold" /> : <X size={14} weight="bold" />}
                    </div>
                    <ForkKnife size={20} className="text-brand-primary" />
                    <span className="text-sm font-medium">Catering Service Available</span>
                  </div>
                  {selected.has_catering && (
                    <div className="mt-4 pl-11 space-y-4">
                      {/* Minimum Guests */}
                      <div className="space-y-1">
                        <Label className="text-xs font-bold text-text-secondary">Minimum Guests for Catering</Label>
                        <Input
                          type="number"
                          value={selected.min_guests}
                          onChange={(e) => updateField(vIdx, 'min_guests', e.target.value)}
                          placeholder="e.g. 100"
                          className="rounded-none border-[#E8E1D5] h-9 w-40"
                          data-testid={`min-guests-input-${vIdx}`}
                        />
                      </div>

                      {/* Tier pricing - meal wise */}
                      {[
                        { key: 'budget', label: 'Budget Friendly', color: 'bg-green-600' },
                        { key: 'premium', label: 'Premium', color: 'bg-purple-600' },
                        { key: 'luxury', label: 'Luxury', color: 'bg-amber-600' },
                      ].map(tier => (
                        <div key={tier.key} className="border border-[#E8E1D5] rounded p-3">
                          <div className="mb-3">
                            <span className={`text-xs px-2 py-0.5 text-white font-bold uppercase tracking-wider ${tier.color}`}>
                              {tier.label}
                            </span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
                            {/* Morning Tiffin - single price */}
                            <div className="space-y-1">
                              <Label className="text-xs font-bold text-text-secondary">Morning Tiffin (with Tea & Coffee)</Label>
                              <Input
                                type="number"
                                value={selected.catering_tiers[tier.key].morning_tiffin}
                                onChange={(e) => updateCateringTier(vIdx, tier.key, 'morning_tiffin', e.target.value)}
                                placeholder="₹ per plate"
                                className="rounded-none border-[#E8E1D5] h-8 text-sm"
                                data-testid={`catering-${tier.key}-tiffin-${vIdx}`}
                              />
                            </div>
                            {/* Juice - single price */}
                            <div className="space-y-1">
                              <Label className="text-xs font-bold text-text-secondary">Juice</Label>
                              <Input
                                type="number"
                                value={selected.catering_tiers[tier.key].juice}
                                onChange={(e) => updateCateringTier(vIdx, tier.key, 'juice', e.target.value)}
                                placeholder="₹ per glass"
                                className="rounded-none border-[#E8E1D5] h-8 text-sm"
                                data-testid={`catering-${tier.key}-juice-${vIdx}`}
                              />
                            </div>
                            {/* Lunch Veg */}
                            <div className="space-y-1">
                              <Label className="text-xs font-bold text-text-secondary">Lunch — Veg</Label>
                              <Input
                                type="number"
                                value={selected.catering_tiers[tier.key].lunch_veg}
                                onChange={(e) => updateCateringTier(vIdx, tier.key, 'lunch_veg', e.target.value)}
                                placeholder="₹ per plate"
                                className="rounded-none border-[#E8E1D5] h-8 text-sm"
                                data-testid={`catering-${tier.key}-lunch-veg-${vIdx}`}
                              />
                            </div>
                            {/* Lunch Non-Veg */}
                            <div className="space-y-1">
                              <Label className="text-xs font-bold text-text-secondary">Lunch — Non-Veg</Label>
                              <Input
                                type="number"
                                value={selected.catering_tiers[tier.key].lunch_nonveg}
                                onChange={(e) => updateCateringTier(vIdx, tier.key, 'lunch_nonveg', e.target.value)}
                                placeholder="₹ per plate"
                                className="rounded-none border-[#E8E1D5] h-8 text-sm"
                                data-testid={`catering-${tier.key}-lunch-nonveg-${vIdx}`}
                              />
                            </div>
                            {/* Evening Snacks - single price */}
                            <div className="space-y-1 md:col-span-2">
                              <Label className="text-xs font-bold text-text-secondary">Evening Snacks (with Tea & Coffee)</Label>
                              <Input
                                type="number"
                                value={selected.catering_tiers[tier.key].evening_snacks}
                                onChange={(e) => updateCateringTier(vIdx, tier.key, 'evening_snacks', e.target.value)}
                                placeholder="₹ per plate"
                                className="rounded-none border-[#E8E1D5] h-8 text-sm md:w-1/2"
                                data-testid={`catering-${tier.key}-snacks-${vIdx}`}
                              />
                            </div>
                            {/* Dinner Veg */}
                            <div className="space-y-1">
                              <Label className="text-xs font-bold text-text-secondary">Dinner (with Dessert) — Veg</Label>
                              <Input
                                type="number"
                                value={selected.catering_tiers[tier.key].dinner_veg}
                                onChange={(e) => updateCateringTier(vIdx, tier.key, 'dinner_veg', e.target.value)}
                                placeholder="₹ per plate"
                                className="rounded-none border-[#E8E1D5] h-8 text-sm"
                                data-testid={`catering-${tier.key}-dinner-veg-${vIdx}`}
                              />
                            </div>
                            {/* Dinner Non-Veg */}
                            <div className="space-y-1">
                              <Label className="text-xs font-bold text-text-secondary">Dinner (with Dessert) — Non-Veg</Label>
                              <Input
                                type="number"
                                value={selected.catering_tiers[tier.key].dinner_nonveg}
                                onChange={(e) => updateCateringTier(vIdx, tier.key, 'dinner_nonveg', e.target.value)}
                                placeholder="₹ per plate"
                                className="rounded-none border-[#E8E1D5] h-8 text-sm"
                                data-testid={`catering-${tier.key}-dinner-nonveg-${vIdx}`}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}

      <div className="sticky bottom-4 bg-sand-50 p-4 border-t border-[#E8E1D5]">
        <Button type="button" onClick={handleSaveAll} disabled={selectedVenues.length === 0}
          className="w-full bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none" data-testid="save-venue-btn">
          Save {selectedVenues.length} Venue Type{selectedVenues.length !== 1 ? 's' : ''}
        </Button>
      </div>
    </div>
  );
};

export default VenueSelector;
